import { TFunction } from "i18next";
import { HexacoWebApiError, HexacoWebOrderStatus } from "../../services/hexacoWebApi";

export const PRE_PAYMENT_PROCESSING_STATUSES: HexacoWebOrderStatus[] = [
  "TNC_ACCEPTED",
  "PDF_UPLOADED",
  "EXTRACTING_SCORES",
  "SCORES_EXTRACTED",
  "ANALYZING",
  "GENERATING_PDF",
];

/** @deprecated Use PRE_PAYMENT_PROCESSING_STATUSES or isPrePaymentProcessingStatus */
export const PROCESSING_STATUSES: HexacoWebOrderStatus[] = [
  ...PRE_PAYMENT_PROCESSING_STATUSES,
  "CHECKOUT_PENDING",
];

export const PAYMENT_PHASE_STATUSES: HexacoWebOrderStatus[] = [
  "REPORT_READY",
  "CHECKOUT_PENDING",
  "PAID",
  "REFUNDED",
];

export const isPaymentPhaseStatus = (
  status: HexacoWebOrderStatus | null,
): boolean => status !== null && PAYMENT_PHASE_STATUSES.includes(status);

export const isPrePaymentProcessingStatus = (
  status: HexacoWebOrderStatus | null,
): boolean =>
  status !== null && PRE_PAYMENT_PROCESSING_STATUSES.includes(status);

export const isRateLimitError = (error: unknown): error is HexacoWebApiError =>
  error instanceof HexacoWebApiError && error.status === 429;

export const getRateLimitErrorMessage = (
  error: unknown,
  t: TFunction,
): string => {
  if (error instanceof HexacoWebApiError && error.status === 429) {
    const minutes = formatRetryAfter(error.retryAfter);
    if (minutes) {
      return t("hexaco.purchase.errors.rateLimitWithMinutes", { minutes });
    }
    return t("hexaco.purchase.errors.rateLimit");
  }
  return t("hexaco.purchase.errors.rateLimit");
};

/** True if the file content starts with the PDF magic header. */
export async function looksLikePdf(file: File): Promise<boolean> {
  const header = new TextDecoder().decode(await file.slice(0, 5).arrayBuffer());
  return header.startsWith("%PDF");
}

export const isInvalidHexacoPdfError = (
  message?: string | null,
  businessStatus?: string | null,
): boolean => {
  if (businessStatus === "NOT_HEXACO_PDF") {
    return true;
  }

  const normalized = message?.trim().toLowerCase() ?? "";
  if (!normalized) {
    return false;
  }

  return (
    normalized.includes("valid hexaco test results") ||
    normalized.includes("invalid hexaco pdf") ||
    normalized.includes("not_hexaco_pdf") ||
    normalized.includes("not hexaco pdf")
  );
};

export const formatRetryAfter = (seconds?: number): string | undefined => {
  if (!seconds || seconds <= 0) {
    return undefined;
  }

  const minutes = Math.ceil(seconds / 60);
  if (minutes <= 1) {
    return "1";
  }

  return String(minutes);
};

export const getApiErrorMessage = (
  error: unknown,
  t: TFunction,
  options?: { maxUploadMb?: number },
): string => {
  if (error instanceof HexacoWebApiError) {
    if (error.status === 429) {
      const minutes = formatRetryAfter(error.retryAfter);
      return minutes
        ? t("hexaco.purchase.errors.rateLimitWithMinutes", { minutes })
        : t("hexaco.purchase.errors.rateLimit");
    }

    if (error.businessStatus === "INVALID_FILE_TYPE") {
      return t("hexaco.purchase.errors.invalidFile");
    }

    if (error.businessStatus === "OVER_FILE_SIZE") {
      return t("hexaco.purchase.errors.fileTooLarge", {
        maxMb: options?.maxUploadMb ?? 10,
      });
    }

    return error.message || t("hexaco.purchase.errors.generic");
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return t("hexaco.purchase.errors.generic");
};

export const getStatusLabelKey = (
  status: HexacoWebOrderStatus,
): string => {
  switch (status) {
    case "EXTRACTING_SCORES":
    case "SCORES_EXTRACTED":
      return "hexaco.purchase.status.extracting";
    case "ANALYZING":
      return "hexaco.purchase.status.analyzing";
    case "GENERATING_PDF":
      return "hexaco.purchase.status.generatingPdf";
    case "REPORT_READY":
      return "hexaco.purchase.status.reportReady";
    case "CHECKOUT_PENDING":
      return "hexaco.purchase.status.checkoutPending";
    case "PAID":
      return "hexaco.purchase.status.paid";
    case "FAILED":
      return "hexaco.purchase.status.failed";
    case "EXPIRED":
      return "hexaco.purchase.status.expired";
    case "REFUNDED":
      return "hexaco.purchase.status.refunded";
    default:
      return "hexaco.purchase.status.processing";
  }
};

const INVALID_FILENAME_CHARS = /[<>:"/\\|?*\u0000-\u001f]/g;

export const getHexacoReportDownloadFilename = (
  customerName?: string | null,
): string => {
  const sanitized = (customerName || "")
    .replace(INVALID_FILENAME_CHARS, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);

  if (!sanitized) {
    return "hexaco-report.pdf";
  }

  return `hexaco-report-${sanitized}.pdf`;
};
